/**
 * AdSense ad initialization helper
 *
 * Uses IntersectionObserver to initialize AdSense ads only when their containers
 * become visible. This handles:
 * - Responsive layouts (hidden sm:block, hidden lg:block, etc.)
 * - Viewport resizing (mobile to desktop transitions)
 * - Lazy loading for better performance
 * - Prevents "No slot size for availableWidth=0" errors
 */

(function() {
    'use strict';

    /**
     * Initialize an AdSense ad when it becomes visible
     * @param {HTMLElement} adElement - The <ins class="adsbygoogle"> element
     */
    function initAdSenseAdImpl(adElement) {
        if (!adElement) {
            console.warn('AdSense: No ad element provided to initAdSenseAd()');
            return;
        }

        // Check if already initialized
        if (adElement.dataset.adsenseInitialized === 'true') {
            return;
        }

        // Mark as initialized immediately to prevent race conditions
        adElement.dataset.adsenseInitialized = 'true';

        // Check if IntersectionObserver is supported
        if (!window.IntersectionObserver) {
            // Fallback for older browsers with resize handling
            var cleanupTimeout;
            var checkAndInit = function() {
                if (isElementVisible(adElement)) {
                    pushAdSenseAd(adElement);
                    window.removeEventListener('resize', checkAndInit);
                    clearTimeout(cleanupTimeout);
                    cleanupTimeout = null;
                }
            };

            window.addEventListener('resize', checkAndInit);
            checkAndInit(); // Initial check

            // Cleanup listener after 60s if element never becomes visible
            cleanupTimeout = setTimeout(function() {
                window.removeEventListener('resize', checkAndInit);
                cleanupTimeout = null;
            }, 60000);
            return;
        }

        // Use IntersectionObserver to initialize when visible
        var hasInitialized = false;
        var cleanupTimeout;
        var observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                // Initialize ad when it becomes visible
                if (entry.isIntersecting && !hasInitialized) {
                    hasInitialized = true;
                    pushAdSenseAd(adElement);

                    // Wait for AdSense to process the push() call before disconnecting
                    setTimeout(function() {
                        observer.disconnect();
                        clearTimeout(cleanupTimeout);
                        observer = null;
                        cleanupTimeout = null;
                    }, 100);
                }
            });
        }, {
            threshold: 0.01, // Trigger when at least 1% is visible
            rootMargin: '0px' // Load exactly when entering viewport
        });

        observer.observe(adElement);

        // Cleanup observer after 60s if element never becomes visible
        cleanupTimeout = setTimeout(function() {
            if (!hasInitialized) {
                observer.disconnect();
                observer = null;
                cleanupTimeout = null;
            }
        }, 60000);
    }

    /**
     * Push ad to AdSense queue
     * @param {HTMLElement} adElement
     */
    function pushAdSenseAd(adElement) {
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
            console.error('AdSense: Failed to initialize ad', e);

            // Send to error tracking if available
            if (window.Sentry && window.Sentry.captureException) {
                window.Sentry.captureException(e, {
                    tags: { component: 'adsense' },
                    extra: {
                        adSlot: adElement.dataset ? adElement.dataset.adSlot : 'unknown',
                        visible: safeIsElementVisible(adElement)
                    }
                });
            }
        }
    }

    /**
     * Safe wrapper for visibility check during error handling
     * @param {HTMLElement} element
     * @returns {boolean|string}
     */
    function safeIsElementVisible(element) {
        try {
            return isElementVisible(element);
        } catch (e) {
            return 'error-checking-visibility';
        }
    }

    /**
     * Check if element is visible in viewport
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    function isElementVisible(element) {
        if (!element) return false;

        // Check CSS visibility
        var style = window.getComputedStyle(element);
        if (style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.opacity === '0') {
            return false;
        }

        // Check dimensions
        var rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        // Check if in viewport
        return (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }

    // Expose global function immediately to handle race conditions
    window.initAdSenseAd = initAdSenseAdImpl;
})();
